import React from 'react';
import { Track } from '../../types/artist';
import { ArtistAvatar } from './ArtistAvatar';
import './TransportControl.css';

export interface TransportControlProps {
  // Track and playback state
  track: Track;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  
  // Playback controls
  onPlayPause: () => void;
  onSeek: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMuteToggle: () => void;
  
  // Optional features
  showCloseButton?: boolean;
  showTrackCounter?: boolean;
  trackCounter?: string; // e.g., "Track 1 of 5"
  onClose?: () => void;
  onArtistClick?: (artistId?: number) => void;
  onReport?: () => void;
  
  // Audio element ref (passed from parent)
  audioRef: React.RefObject<HTMLAudioElement | null>;
  
  // Styling
  className?: string;
  variant?: 'default' | 'minimal'; // minimal for simpler layouts
}

export const TransportControl: React.FC<TransportControlProps> = ({
  track,
  isPlaying,
  currentTime,
  duration,
  volume,
  isMuted,
  onPlayPause,
  onSeek,
  onVolumeChange,
  onMuteToggle,
  showCloseButton = false,
  showTrackCounter = false,
  trackCounter,
  onClose,
  onArtistClick,
  onReport,
  audioRef,
  className = '',
  variant = 'default'
}) => {
  
  // Format time from seconds to mm:ss
  const formatTime = (seconds: number): string => {
    // Handle invalid or non-finite values
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleArtistClick = () => {
    if (onArtistClick && track.artist?.id) {
      onArtistClick(track.artist.id);
    }
  };

  const getVolumeIcon = (): string => {
    if (isMuted) return '🔇';
    if (volume > 0.5) return '🔊';
    if (volume > 0) return '🔉';
    return '🔈';
  };

  const playerClassName = `transport-control ${variant} ${className}`;

  return (
    <div className={playerClassName}>
      {/* Player Header */}
      <div className="transport-header">
        <h4>Now Playing</h4>
        <div className="transport-header-actions">
          {showTrackCounter && trackCounter && (
            <span className="track-counter">{trackCounter}</span>
          )}
          {onReport && (
            <button 
              className="report-btn"
              onClick={onReport}
              title="Report this track"
            >
              🚩
            </button>
          )}
          {showCloseButton && onClose && (
            <button 
              className="close-player-btn"
              onClick={onClose}
              title="Close player"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      
      {/* Player Controls */}
      <div className="transport-controls">
        {/* Artist Info */}
        <div 
          className={`transport-artist-info ${onArtistClick ? 'clickable' : ''}`}
          onClick={onArtistClick ? handleArtistClick : undefined}
          title={onArtistClick ? 'View artist profile' : undefined}
        >
          <ArtistAvatar
            artistId={track.artist?.id}
            artistName={track.artist?.name}
            size="medium"
            className="transport-artist-avatar"
          />
          <div className="transport-track-info">
            <span className="transport-track-title">{track.title}</span>
            <span className="transport-artist-name">
              by {track.artist?.name || 'Unknown Artist'}
            </span>
          </div>
        </div>
        
        {/* Play/Pause Button */}
        <button 
          className="transport-play-pause-btn" 
          onClick={onPlayPause}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? '⏸️' : '▶️'}
        </button>
        
        {/* Progress Container */}
        <div className="transport-progress-container">
          <span className="transport-time-display">{formatTime(currentTime)}</span>
          <input
            type="range"
            className="transport-progress-bar"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={onSeek}
            title="Seek"
          />
          <span className="transport-time-display">{formatTime(duration)}</span>
        </div>
        
        {/* Volume Control */}
        <div className="transport-volume-control">
          <button 
            className="transport-volume-btn" 
            onClick={onMuteToggle}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {getVolumeIcon()}
          </button>
          <input
            type="range"
            className="transport-volume-slider"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={onVolumeChange}
            title="Volume"
          />
        </div>
      </div>

      {/* Hidden Audio Element */}
      <audio ref={audioRef} />
    </div>
  );
};